/**
 * useFederatedModule — loads a named module from the orchestrator registry at runtime.
 *
 * Usage:
 *   const { module, loading, error } = useFederatedModule<MyModuleType>('vpc-dashboard');
 *
 * Guarantees:
 *   • The module is loaded exactly once per session (module-level Map cache).
 *   • Concurrent calls from multiple components share the same in-flight request.
 *   • Re-renders after load return the cached module without re-fetching.
 *
 * Logging: All module loading events are logged with the [Federation] prefix.
 */
import { loadRemote } from '@module-federation/runtime';
import { useEffect, useRef, useState } from 'react';

import { useRegistryContext } from './FederatedRegistry';

const LOG_PREFIX = '[Federation]';

/**
 * loadRemote with a short retry. The orchestrator gateway has intermittently
 * answered 503 to bursts of script requests; a retry after a short pause is a
 * far cheaper guard than the old serial preload warm-up (which cost ~12–16 s).
 */
async function loadRemoteWithRetry<T>(remotePath: string, attempts = 3, delayMs = 500): Promise<T | null | undefined> {
  for (let i = 1; ; i++) {
    try {
      return await loadRemote<T>(remotePath);
    } catch (err) {
      if (i >= attempts) throw err;
      console.warn(`${LOG_PREFIX} loadRemote("${remotePath}") attempt ${i} failed — retrying in ${delayMs * i}ms`, err);
      await new Promise((r) => setTimeout(r, delayMs * i));
    }
  }
}

/** Module-level cache — survives re-renders, cleared only on hard page reload. */
const moduleCache = new Map<string, unknown>();

/** Module-level error cache — prevents retrying a permanently failed module. */
const errorCache = new Map<string, Error>();

export interface FederatedModuleResult<T> {
  module: T | undefined;
  loading: boolean;
  error: Error | null;
}

/**
 * Returns the loaded module definition object, loading state, and any error.
 *
 * @param name - The module name as registered in the orchestrator (e.g. "vpc-dashboard").
 */
export function useFederatedModule<T = Record<string, unknown>>(name: string): FederatedModuleResult<T> {
  const { entries, loading: registryLoading, error: registryError } = useRegistryContext();

  const [module, setModule] = useState<T | undefined>(() => moduleCache.get(name) as T | undefined);
  const [error, setError] = useState<Error | null>(() => errorCache.get(name) ?? null);
  const status = useRef<'idle' | 'loading' | 'done' | 'error'>(
    moduleCache.has(name) ? 'done' : errorCache.has(name) ? 'error' : 'idle',
  );

  useEffect(() => {
    // Already loaded, failed, or in flight
    if (status.current !== 'idle') return;
    // Registry still loading
    if (registryLoading) return;
    // Registry fetch failed — propagate as module error
    if (registryError) {
      const err = new Error(`Registry unavailable: ${registryError}`);
      console.error(`${LOG_PREFIX} [${name}] Cannot load — registry failed: ${registryError}`);
      errorCache.set(name, err);
      setError(err);
      status.current = 'error';
      return;
    }
    // Registry loaded but empty or module not found
    if (entries.length === 0) return;

    const entry = entries.find((e) => e.name === name);
    if (!entry) {
      const available = entries.map((e) => e.name).join(', ');
      const err = new Error(`"${name}" is not in the registry. Available: ${available}`);
      console.error(`${LOG_PREFIX} [${name}] Module not found in registry. Available modules: ${available}`);
      errorCache.set(name, err);
      setError(err);
      status.current = 'error';
      return;
    }

    status.current = 'loading';
    // MF resolves '<remote>/<expose>' by prefixing the expose with './' itself,
    // so 'vpc-dashboard/app' → container.get('./app'). Do NOT write '/./app' here.
    const remotePath = `${name}/app`;
    const version = entry.versions[0]?.version ?? 'unknown';
    console.info(`${LOG_PREFIX} [${name}] Loading remote module v${version} via loadRemote("${remotePath}")...`);
    const loadStart = performance.now();

    loadRemoteWithRetry<{ default: () => T }>(remotePath)
      .then((mod) => {
        const elapsed = Math.round(performance.now() - loadStart);

        if (!mod) {
          throw new Error(`loadRemote("${remotePath}") returned null/undefined`);
        }

        const result = typeof mod.default === 'function' ? mod.default() : (mod as unknown as T);
        moduleCache.set(name, result);
        setModule(result);
        status.current = 'done';

        // Log what was resolved
        const exportKeys = result && typeof result === 'object' ? Object.keys(result) : [];
        console.info(
          `${LOG_PREFIX} [${name}] ✅ Loaded successfully (${elapsed}ms)`,
          { version, exports: exportKeys.length, keys: exportKeys.slice(0, 10) },
        );
      })
      .catch((err: Error) => {
        const elapsed = Math.round(performance.now() - loadStart);
        console.error(
          `${LOG_PREFIX} [${name}] ❌ loadRemote failed after ${elapsed}ms:`,
          err.message,
          err,
        );
        errorCache.set(name, err);
        setError(err);
        status.current = 'error';
      });
  }, [entries, registryLoading, registryError, name]);

  // 'idle' only counts as loading while there is still work pending (registry in
  // flight, or entries present that the effect will pick up). If the registry is
  // done and empty (no orchestrator URL / nothing published), report not-loading so
  // consumers fall back to static immediately instead of waiting for a timeout.
  const loading = registryLoading || status.current === 'loading' || (status.current === 'idle' && entries.length > 0);

  return { module, loading, error };
}
